import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Plus, 
  Trash2, 
  Lock, 
  Eye, 
  Send, 
  ArrowLeft,
  User,
  Calendar,
  MessageCircle,
  Settings,
  X,
  Check
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Forum = () => {
  const { isSindacatoAdmin, hasPermesso, user } = useAuth();
  const isAdmin = isSindacatoAdmin();
  const canManageSezioni = () => isAdmin || hasPermesso('gestisci_sezioni_forum');
  
  const [sezioni, setSezioni] = useState([]);
  const [ruoli, setRuoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSezione, setSelectedSezione] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  
  // Dialog states
  const [sezioneDialogOpen, setSezioneDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [editingSezione, setEditingSezione] = useState(null);
  
  // Form states
  const [sezioneForm, setSezioneForm] = useState({
    nome: '',
    descrizione: '',
    icona: 'MessageSquare',
    privata: false,
    ordine: 0,
    ruoli_visualizzazione: [],
    ruoli_creazione_ticket: [],
    ruoli_risposta: [],
    ruoli_chiusura: []
  });
  
  const [ticketForm, setTicketForm] = useState({
    titolo: '',
    contenuto: '',
    categoria: 'Generale'
  });
  
  const [risposta, setRisposta] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sezioniRes, ruoliRes] = await Promise.all([
        axios.get(`${API_URL}/forum/sezioni`),
        axios.get(`${API_URL}/ruoli-azienda`)
      ]);
      setSezioni(sezioniRes.data);
      setRuoli(ruoliRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento del forum');
    } finally {
      setLoading(false);
    }
  };

  const fetchSezioneTickets = async (sezione) => {
    try {
      const res = await axios.get(`${API_URL}/forum/sezioni/${sezione.id}/tickets`);
      setSelectedSezione(res.data.sezione);
      setTickets(res.data.tickets);
      setSelectedTicket(null);
      setTicketDetail(null);
    } catch (error) {
      toast.error('Errore nel caricamento dei ticket');
    }
  };

  // Carica utenti online nel forum (pagina principale)
  const fetchOnlineUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/forum/users-online`);
      setOnlineUsers(res.data);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  // Registra presenza e aggiorna ogni 30 secondi quando si è nel forum
  useEffect(() => {
    // Registra presenza all'apertura del forum
    const registerPresence = async () => {
      try {
        await axios.post(`${API_URL}/forum/presence`);
        fetchOnlineUsers();
      } catch (error) {
        console.error('Error registering presence:', error);
      }
    };
    
    registerPresence();
    
    const interval = setInterval(async () => {
      try {
        await axios.post(`${API_URL}/forum/presence`);
        fetchOnlineUsers();
      } catch (error) {
        console.error('Error updating presence:', error);
      }
    }, 30000);
    
    return () => {
      clearInterval(interval);
      // Rimuovi presenza quando si esce dal forum
      axios.delete(`${API_URL}/forum/presence`).catch(() => {});
    };
  }, []);

  const fetchTicketDetail = async (ticket) => {
    try {
      const res = await axios.get(`${API_URL}/forum/tickets/${ticket.id}`);
      setTicketDetail(res.data);
      setSelectedTicket(ticket);
    } catch (error) {
      toast.error('Errore nel caricamento del ticket');
    }
  };

  const handleCreateSezione = async () => {
    try {
      if (editingSezione) {
        await axios.put(`${API_URL}/forum/sezioni/${editingSezione.id}`, sezioneForm);
        toast.success('Sezione aggiornata');
      } else {
        await axios.post(`${API_URL}/forum/sezioni`, sezioneForm);
        toast.success('Sezione creata');
      }
      setSezioneDialogOpen(false);
      resetSezioneForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteSezione = async (id) => {
    if (!window.confirm('Eliminare questa sezione e tutti i suoi ticket?')) return;
    try {
      await axios.delete(`${API_URL}/forum/sezioni/${id}`);
      toast.success('Sezione eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCreateTicket = async () => {
    try {
      await axios.post(`${API_URL}/forum/tickets`, {
        ...ticketForm,
        sezione_id: selectedSezione.id
      });
      toast.success('Ticket creato');
      setTicketDialogOpen(false);
      setTicketForm({ titolo: '', contenuto: '', categoria: 'Generale' });
      fetchSezioneTickets(selectedSezione);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleSendRisposta = async () => {
    if (!risposta.trim()) return;
    try {
      await axios.post(`${API_URL}/forum/tickets/${selectedTicket.id}/risposte`, {
        contenuto: risposta
      });
      toast.success('Risposta inviata');
      setRisposta('');
      fetchTicketDetail(selectedTicket);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleChangeTicketStato = async (stato) => {
    try {
      await axios.patch(`${API_URL}/forum/tickets/${selectedTicket.id}/stato?stato=${stato}`);
      toast.success(`Ticket ${stato.toLowerCase()}`);
      fetchTicketDetail(selectedTicket);
      fetchSezioneTickets(selectedSezione);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm('Eliminare questo ticket?')) return;
    try {
      await axios.delete(`${API_URL}/forum/tickets/${selectedTicket.id}`);
      toast.success('Ticket eliminato');
      setSelectedTicket(null);
      setTicketDetail(null);
      fetchSezioneTickets(selectedSezione);
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetSezioneForm = () => {
    setSezioneForm({
      nome: '',
      descrizione: '',
      icona: 'MessageSquare',
      privata: false,
      ordine: 0,
      ruoli_visualizzazione: [],
      ruoli_creazione_ticket: [],
      ruoli_risposta: [],
      ruoli_chiusura: []
    });
    setEditingSezione(null);
  };

  const openEditSezione = (sez) => {
    setEditingSezione(sez);
    setSezioneForm({
      nome: sez.nome,
      descrizione: sez.descrizione,
      icona: sez.icona || 'MessageSquare',
      privata: sez.privata || false,
      ordine: sez.ordine || 0,
      ruoli_visualizzazione: sez.ruoli_visualizzazione || [],
      ruoli_creazione_ticket: sez.ruoli_creazione_ticket || [],
      ruoli_risposta: sez.ruoli_risposta || [],
      ruoli_chiusura: sez.ruoli_chiusura || []
    });
    setSezioneDialogOpen(true);
  };

  const toggleRuolo = (field, ruoloId) => {
    const current = sezioneForm[field];
    if (current.includes(ruoloId)) {
      setSezioneForm({ ...sezioneForm, [field]: current.filter(r => r !== ruoloId) });
    } else {
      setSezioneForm({ ...sezioneForm, [field]: [...current, ruoloId] });
    }
  };

  // Helper per determinare se un utente è Staff
  const isUserStaff = (ticket) => {
    return ticket.autore_is_staff || (ticket.autore_azienda && ticket.autore_azienda.toLowerCase().includes('staff'));
  };

  // Funzione per ottenere i badge nell'ordine corretto: prima STAFF, poi ruolo/lavoro
  const getUserBadges = (ruolo, azienda, isStaff, staffRuolo) => {
    const badges = [];
    
    // Prima mostra la targhetta STAFF se l'utente è staff
    if (isStaff || (azienda && azienda.toLowerCase().includes('staff'))) {
      badges.push(
        <Badge key="staff" className="text-xs bg-red-500 text-white font-bold border-0 shadow-sm">
          STAFF
        </Badge>
      );
      // Poi mostra il ruolo specifico dello staff se presente
      if (staffRuolo) {
        badges.push(
          <Badge key="staff-ruolo" className="text-xs bg-red-100 text-red-700 border border-red-200">
            {staffRuolo}
          </Badge>
        );
      } else if (ruolo && ruolo !== 'Staff') {
        badges.push(
          <Badge key="ruolo" className="text-xs bg-red-100 text-red-700 border border-red-200">
            {ruolo}
          </Badge>
        );
      }
    } else if (ruolo === 'Admin Sistema' || ruolo === 'SINDACATO_ADMIN') {
      // Admin in ambra
      badges.push(
        <Badge key="admin" className="text-xs bg-amber-500 text-white font-bold border-0 shadow-sm">
          ADMIN
        </Badge>
      );
    } else if (ruolo) {
      // Altri ruoli normali
      badges.push(
        <Badge key="ruolo" variant="outline" className="text-xs bg-slate-100">
          {ruolo}
        </Badge>
      );
    }
    
    // Mostra l'azienda se non è staff (per staff la targhetta è già mostrata)
    if (azienda && !azienda.toLowerCase().includes('staff') && badges.length > 0) {
      badges.push(
        <Badge key="azienda" variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
          {azienda}
        </Badge>
      );
    }
    
    return badges;
  };

  // Manteniamo la vecchia funzione per compatibilità ma usiamo la nuova logica
  const getRoleBadge = (ruolo, azienda, isStaff) => {
    // Staff in rosso
    if (isStaff || (azienda && azienda.toLowerCase().includes('staff'))) {
      return <Badge className="text-xs bg-red-100 text-red-700 border border-red-200">{ruolo}</Badge>;
    }
    // Admin in ambra
    if (ruolo === 'Admin Sistema') {
      return <Badge className="text-xs bg-amber-100 text-amber-700 border border-amber-200">{ruolo}</Badge>;
    }
    // Altri
    return <Badge variant="outline" className="text-xs bg-slate-100">{ruolo}</Badge>;
  };

  const getStatoBadge = (stato) => {
    const variants = {
      'IN_VALUTAZIONE': 'bg-amber-100 text-amber-800',
      'RISOLTO': 'bg-green-100 text-green-800',
      'ACCETTATO': 'bg-green-100 text-green-800',
      'RIFIUTATO': 'bg-red-100 text-red-800',
      // Legacy
      'APERTO': 'bg-amber-100 text-amber-800',
      'CHIUSO': 'bg-slate-100 text-slate-800',
      'IN_ATTESA': 'bg-amber-100 text-amber-800'
    };
    const labels = {
      'IN_VALUTAZIONE': 'In Valutazione',
      'RISOLTO': 'Risolto',
      'ACCETTATO': 'Accettato',
      'RIFIUTATO': 'Rifiutato',
      'APERTO': 'In Valutazione',
      'CHIUSO': 'Chiuso',
      'IN_ATTESA': 'In Attesa'
    };
    return <Badge className={variants[stato] || 'bg-slate-100 text-slate-800'}>{labels[stato] || stato}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Vista dettaglio ticket
  if (ticketDetail) {
    return (
      <div data-testid="forum-ticket-detail" className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => { setTicketDetail(null); setSelectedTicket(null); }}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna ai ticket
          </Button>
        </div>
        
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-xl">{ticketDetail.ticket.titolo}</CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1 flex-wrap">
                  <User className="w-4 h-4" />
                  <span className="font-medium">@{ticketDetail.ticket.autore_username || ticketDetail.ticket.autore_nome}</span>
                  {getUserBadges(
                    ticketDetail.ticket.autore_ruolo, 
                    ticketDetail.ticket.autore_azienda, 
                    ticketDetail.ticket.autore_is_staff,
                    ticketDetail.ticket.autore_staff_ruolo
                  )}
                  <span>•</span>
                  <Calendar className="w-4 h-4" />
                  {new Date(ticketDetail.ticket.created_at).toLocaleDateString('it-IT')}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {getStatoBadge(ticketDetail.ticket.stato)}
                {ticketDetail.can_close && (
                  <>
                    {ticketDetail.ticket.stato === 'IN_VALUTAZIONE' && (
                      <>
                        <Button size="sm" variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100" onClick={() => handleChangeTicketStato('RISOLTO')}>
                          <Check className="w-4 h-4 mr-1" />
                          Risolto
                        </Button>
                        <Button size="sm" variant="outline" className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100" onClick={() => handleChangeTicketStato('ACCETTATO')}>
                          <Check className="w-4 h-4 mr-1" />
                          Accettato
                        </Button>
                        <Button size="sm" variant="outline" className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100" onClick={() => handleChangeTicketStato('RIFIUTATO')}>
                          <X className="w-4 h-4 mr-1" />
                          Rifiutato
                        </Button>
                      </>
                    )}
                    {(ticketDetail.ticket.stato === 'RISOLTO' || ticketDetail.ticket.stato === 'ACCETTATO' || ticketDetail.ticket.stato === 'RIFIUTATO') && (
                      <Button size="sm" variant="outline" onClick={() => handleChangeTicketStato('IN_VALUTAZIONE')}>
                        Riapri
                      </Button>
                    )}
                  </>
                )}
                {(ticketDetail.is_owner || isAdmin || hasPermesso('modera_forum')) && (
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={handleDeleteTicket}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">
              {ticketDetail.ticket.contenuto}
            </div>
          </CardContent>
        </Card>

        {/* Risposte */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Risposte ({ticketDetail.risposte.length})
          </h3>
          
          {ticketDetail.risposte.map((ris) => (
            <Card key={ris.id} className={`border-l-4 ${ris.autore_is_staff ? 'border-l-red-500 bg-red-50/30' : 'border-l-blue-400'}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
                    <User className="w-4 h-4" />
                    <span className="font-medium">@{ris.autore_username || ris.autore_nome}</span>
                    {getUserBadges(ris.autore_ruolo, ris.autore_azienda, ris.autore_is_staff, ris.autore_staff_ruolo)}
                    <span>•</span>
                    <Calendar className="w-4 h-4" />
                    {new Date(ris.created_at).toLocaleString('it-IT')}
                  </div>
                </div>
                <p className="whitespace-pre-wrap">{ris.contenuto}</p>
              </CardContent>
            </Card>
          ))}

          {/* Form risposta */}
          {ticketDetail.ticket.stato !== 'CHIUSO' && ticketDetail.can_respond && (
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <Textarea
                    value={risposta}
                    onChange={(e) => setRisposta(e.target.value)}
                    placeholder="Scrivi una risposta..."
                    rows={3}
                  />
                  <Button onClick={handleSendRisposta} disabled={!risposta.trim()}>
                    <Send className="w-4 h-4 mr-2" />
                    Invia Risposta
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Vista lista ticket di una sezione
  if (selectedSezione) {
    return (
      <div data-testid="forum-sezione-detail" className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setSelectedSezione(null); setTickets([]); }}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alle sezioni
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{selectedSezione.nome}</h1>
              <p className="text-slate-600">{selectedSezione.descrizione}</p>
            </div>
          </div>
          {selectedSezione.can_create && (
            <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Ticket
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nuovo Ticket</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Titolo</Label>
                    <Input
                      value={ticketForm.titolo}
                      onChange={(e) => setTicketForm({ ...ticketForm, titolo: e.target.value })}
                      placeholder="Titolo del ticket"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select
                      value={ticketForm.categoria}
                      onValueChange={(v) => setTicketForm({ ...ticketForm, categoria: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Generale">Generale</SelectItem>
                        <SelectItem value="Roleplay">Roleplay</SelectItem>
                        <SelectItem value="Bug">Bug</SelectItem>
                        <SelectItem value="Richiesta">Richiesta</SelectItem>
                        <SelectItem value="Altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Contenuto</Label>
                    <Textarea
                      value={ticketForm.contenuto}
                      onChange={(e) => setTicketForm({ ...ticketForm, contenuto: e.target.value })}
                      placeholder="Descrivi la tua richiesta..."
                      rows={5}
                    />
                  </div>
                  <Button onClick={handleCreateTicket} className="w-full">
                    Crea Ticket
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {tickets.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <MessageSquare className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">Nessun ticket in questa sezione</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <Card 
                key={ticket.id} 
                className={`hover:shadow-md transition-shadow cursor-pointer ${ticket.autore_is_staff ? 'border-l-4 border-l-red-500' : ''}`}
                onClick={() => fetchTicketDetail(ticket)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatoBadge(ticket.stato)}
                        <Badge variant="outline">{ticket.categoria}</Badge>
                      </div>
                      <h3 className="font-semibold">{ticket.titolo}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          @{ticket.autore_username || ticket.autore_nome}
                        </span>
                        {getUserBadges(ticket.autore_ruolo, ticket.autore_azienda, ticket.autore_is_staff, ticket.autore_staff_ruolo)}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(ticket.created_at).toLocaleDateString('it-IT')}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {ticket.risposte_count || 0} risposte
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vista principale - Lista sezioni
  return (
    <div data-testid="forum-page" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <MessageSquare className="w-8 h-8" />
            Forum
          </h1>
          <p className="text-slate-600 mt-1">
            Discussioni, richieste e assistenza
          </p>
        </div>
        {canManageSezioni() && (
          <Dialog open={sezioneDialogOpen} onOpenChange={(open) => { setSezioneDialogOpen(open); if (!open) resetSezioneForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Sezione
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSezione ? 'Modifica Sezione' : 'Nuova Sezione'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome Sezione</Label>
                    <Input
                      value={sezioneForm.nome}
                      onChange={(e) => setSezioneForm({ ...sezioneForm, nome: e.target.value })}
                      placeholder="Es: Richieste generali"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ordine</Label>
                    <Input
                      type="number"
                      value={sezioneForm.ordine}
                      onChange={(e) => setSezioneForm({ ...sezioneForm, ordine: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Textarea
                    value={sezioneForm.descrizione}
                    onChange={(e) => setSezioneForm({ ...sezioneForm, descrizione: e.target.value })}
                    placeholder="Descrivi lo scopo della sezione..."
                    rows={2}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={sezioneForm.privata}
                    onCheckedChange={(checked) => setSezioneForm({ ...sezioneForm, privata: checked })}
                  />
                  <Label>Sezione privata (visibile solo a ruoli specifici)</Label>
                </div>

                {/* Permessi Ruoli */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-semibold">Permessi per Ruolo</h4>
                  
                  {['ruoli_visualizzazione', 'ruoli_risposta', 'ruoli_chiusura'].map((field) => (
                    <div key={field} className="space-y-2">
                      <Label className="text-sm">
                        {field === 'ruoli_visualizzazione' && 'Chi può visualizzare'}
                        {field === 'ruoli_risposta' && 'Chi può rispondere'}
                        {field === 'ruoli_chiusura' && 'Chi può aprire/chiudere'}
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {ruoli.map((ruolo) => (
                          <Badge
                            key={ruolo.id}
                            variant={sezioneForm[field].includes(ruolo.id) ? "default" : "outline"}
                            className="cursor-pointer"
                            onClick={() => toggleRuolo(field, ruolo.id)}
                          >
                            {ruolo.nome}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-slate-500 italic">
                    Nota: Tutti possono creare ticket, senza restrizioni.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => { setSezioneDialogOpen(false); resetSezioneForm(); }}>
                    Annulla
                  </Button>
                  <Button onClick={handleCreateSezione}>
                    {editingSezione ? 'Salva Modifiche' : 'Crea Sezione'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Box Utenti Online */}
      {onlineUsers.length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-slate-700">Online nel Forum ({onlineUsers.length}):</span>
              </div>
              {onlineUsers.map((u) => (
                <div
                  key={u.user_id}
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-full ${
                    u.is_staff 
                      ? 'bg-red-100 text-red-700 border border-red-300' 
                      : u.is_admin 
                        ? 'bg-amber-100 text-amber-700 border border-amber-300' 
                        : 'bg-white text-slate-700 border border-slate-200'
                  }`}
                >
                  <span>{u.nome}</span>
                  <span className="text-xs opacity-70">(@{u.nickname})</span>
                  {u.is_staff && u.staff_ruolo && (
                    <Badge className="bg-red-200 text-red-800 text-xs ml-1">{u.staff_ruolo}</Badge>
                  )}
                  {u.is_admin && !u.is_staff && (
                    <Badge className="bg-amber-200 text-amber-800 text-xs ml-1">Admin</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {sezioni.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessuna sezione disponibile</p>
            {canManageSezioni() && (
              <p className="text-sm text-slate-400">Crea la prima sezione del forum</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sezioni.map((sez) => (
            <Card 
              key={sez.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => fetchSezioneTickets(sez)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{sez.nome}</h3>
                        {sez.privata && <Lock className="w-4 h-4 text-slate-400" />}
                      </div>
                      <p className="text-sm text-slate-600">{sez.descrizione}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold">{sez.ticket_count || 0}</p>
                      <p className="text-xs text-slate-500">Discussioni</p>
                    </div>
                    {canManageSezioni() && (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => openEditSezione(sez)}>
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteSezione(sez.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {sez.ultimo_ticket && (
                  <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm text-slate-500">
                    <span>Ultimo: {sez.ultimo_ticket.titolo}</span>
                    <span>•</span>
                    <span>da @{sez.ultimo_ticket.autore_username || sez.ultimo_ticket.autore_nome}</span>
                    <span>•</span>
                    <span>{new Date(sez.ultimo_ticket.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Forum;
